import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import {
  GetStoresDocument,
  GetWarehousesDocument,
} from '../../../generated/graphql'
import { GetWarehousesQuery } from '../../../generated/graphql'

export type WarehouseOption = GetWarehousesQuery['allWarehouses'][number] & {
  storeId: number
  storeName: string
}

export const useWarehouseOptions = () => {
  const { data: storesData, loading: loadingStores } =
    useQuery(GetStoresDocument)
  const { data: warehousesData, loading: loadingWarehouses } = useQuery(
    GetWarehousesDocument,
  )
  const allWarehouses = warehousesData?.allWarehouses

  const stores = useMemo(
    () => storesData?.stores?.filter((s) => s.warehouses?.length),
    [storesData?.stores],
  )

  const warehouseOptions = useMemo(() => {
    if (!stores || !allWarehouses) return []

    const warehousesWithStore = stores
      .sort((a, b) => a.name.localeCompare(b.name))
      .flatMap((store) =>
        allWarehouses
          .filter((warehouse) => warehouse.storeid === store.id)
          .map((warehouse) => ({
            ...warehouse,
            storeId: store.id,
            storeName: store.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      )

    const warehousesWithoutStore = allWarehouses
      .filter(
        (warehouse) => !stores.some((store) => store.id === warehouse.storeid),
      )
      .map((warehouse) => ({
        ...warehouse,
        storeId: 0,
        storeName: '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

    return [...warehousesWithStore, ...warehousesWithoutStore]
  }, [stores, allWarehouses])

  return { warehouseOptions, isLoading: loadingStores || loadingWarehouses }
}
